import clsx from 'clsx';
import { EQuestionType, QUESTION_DISPLAY_MODE } from '@hubertai/screening-question-types';
import type { TSingleSelectOption } from '@hubertai/screening-question-types';
import { toast } from 'sonner';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CreateOptionHeader from '../prompts/Selects/CreateOptionHeader';
import CreateOption from '../prompts/Selects/CreateOption';
import CreateOptionOptions from '../prompts/Selects/CreateOptionOptions';
import CreateRankingOptionHeader from '../prompts/Ranking/CreateRankingOptionHeader';
import CreateRankingOption from '../prompts/Ranking/CreateRankingOption';
import { ECreateQuestionMode, useQuestionEditorContext } from '../context/create-question.context';
import SelectionCountPreview from '../prompts/Selects/SelectionCountPreview';
import CreateOpen from '../prompts/Open';
import CreateInfo from '../prompts/Info';
import CreateFormHeader from '../prompts/Form/CreateFormHeader';
import FormElement from '../prompts/Form/UpsertFormElements/FormElement';
import PasteOptionsList from '../prompts/Selects/PasteOptionsList';
import HelpTextInput from './HelpTextInput/HelpTextInput';
import RandomizeOrderSwitch from './RandomizeOrderSwitch';
import QuestionVoiceSettings from './QuestionVoiceSettings';
import TitleInput from './TitleInput';
import NumberSettingInput from './NumberSettingInput';
import RankingModeSelect from './RankingModeSelect';
import SingleSelectDisplayModeSelect from './SingleSelectDisplayModeSelect';
import OptionsPagination from './OptionsPagination';
import CreateRecording from './CreateRecording';
import CreateScheduling from './CreateScheduling';
import { useTemplateId } from 'hooks/useTemplate';
import MutedText from 'components/common/MutedText';
import HubertInput from 'components/HubertUI/HubertInput';
import {
  selectQuestionDisplayMode,
  selectQuestionIsReadonly,
  selectQuestionLanguage,
  selectQuestionMode,
  selectQuestionOptions,
  selectShowDisabledOptions,
} from 'rtk/questionEditorSelector';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import TextBubbleDragAndDrop from 'components/TextBubbleDragAndDrop';
import { dragAndDropForm, dragAndDropOption } from 'rtk/Question.Editor.Slice';
import { updateInTemplateOptionOrder } from 'rtk/questionEditorThunks';
import { selectTemplateOptions } from 'rtk/TemplateSelector';
import type { RootState } from 'app/store';
import { useUser } from 'hooks/useUser';
import { isDeveloper } from 'types/user.types';
import { useQuestionEvaluation } from 'hooks/useQuestionEvaluation';
import { useQuestionRules } from 'hooks/useQuestionRules';
import { useInTemplateEditAndUneditable } from 'hooks/useInTemplateEditAndUneditable';

// Above this many options the editor pages them, so only one page of rows is ever mounted.
const OPTIONS_PAGE_SIZE = 50;

// Accent- and case-insensitive, so "alava" finds "Álava".
const normalizeForSearch = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const TypeSpecificEditor = () => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const templateId = useTemplateId();
  const { questionId } = useQuestionEditorContext();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const isReadOnly = useAppSelector((state) => selectQuestionIsReadonly(state, questionId));
  const questionLanguage = useAppSelector((state) => selectQuestionLanguage(state, questionId));
  const options = useAppSelector((state) => selectQuestionOptions(state, questionId));
  const type = useAppSelector((state) => state.questionEditor.byId[questionId].type);
  const forms = useAppSelector((state) => state.questionEditor.byId[questionId].forms);
  const errors = useAppSelector((state) => state.questionEditor.byId[questionId].formErrors);
  const templateOptions = useAppSelector((state: RootState) =>
    questionId ? selectTemplateOptions(state, questionId) : undefined,
  );
  const showDisabledOptions = useAppSelector((state) => selectShowDisabledOptions(state));
  const displayMode = useAppSelector((state) => selectQuestionDisplayMode(state, questionId));
  const [pendingHiddenOptionIds, setPendingHiddenOptionIds] = useState<Record<string, true>>({});
  const [optionsPage, setOptionsPage] = useState(0);
  const [optionsQuery, setOptionsQuery] = useState('');
  const optionsListRef = useRef<HTMLDivElement>(null);
  const previousOptionsCountRef = useRef<{ questionId: string; count: number }>({
    questionId,
    count: options?.length ?? 0,
  });
  const previousEnabledByIdRef = useRef<Record<string, boolean | undefined>>({});
  const hideTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const inTemplateEdit = mode === ECreateQuestionMode.InTemplateEdit;
  const user = useUser();
  // Question-level, not per-option: computed once and handed to every CreateOption.
  const { evaluation } = useQuestionEvaluation(questionId);
  const { list: currentRules } = useQuestionRules(questionId);
  const inTemplateEditAndUneditable = useInTemplateEditAndUneditable(questionId, mode);
  useEffect(() => {
    const count = options?.length ?? 0;
    const previous = previousOptionsCountRef.current;
    previousOptionsCountRef.current = { questionId, count };
    if (previous.questionId !== questionId) {
      setOptionsPage(0);
      setOptionsQuery('');
    } else if (count > previous.count) {
      // Added or pasted options land at the end, so follow them (a search would hide them).
      setOptionsQuery('');
      setOptionsPage(Math.max(0, Math.ceil(count / OPTIONS_PAGE_SIZE) - 1));
    }
  }, [questionId, options?.length]);
  useEffect(() => {
    return () => {
      Object.values(hideTimeoutsRef.current).forEach((timeoutId) => clearTimeout(timeoutId));
      hideTimeoutsRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (!options) return;

    const nextEnabledById: Record<string, boolean | undefined> = {};
    const isTemplateHideMode = inTemplateEdit && !showDisabledOptions;

    options.forEach((option) => {
      const optionId = option.id;
      const currentEnabled = option.enabled?.[questionLanguage];
      const previousEnabled = previousEnabledByIdRef.current[optionId];

      nextEnabledById[optionId] = currentEnabled;

      if (isTemplateHideMode && previousEnabled === true && currentEnabled === false) {
        setPendingHiddenOptionIds((prev) => ({ ...prev, [optionId]: true }));

        if (hideTimeoutsRef.current[optionId]) {
          clearTimeout(hideTimeoutsRef.current[optionId]);
        }

        hideTimeoutsRef.current[optionId] = setTimeout(() => {
          setPendingHiddenOptionIds((prev) => {
            if (!prev[optionId]) return prev;
            const next = { ...prev };
            delete next[optionId];
            return next;
          });
          delete hideTimeoutsRef.current[optionId];
        }, 500);
      }

      if (currentEnabled !== false && hideTimeoutsRef.current[optionId]) {
        clearTimeout(hideTimeoutsRef.current[optionId]);
        delete hideTimeoutsRef.current[optionId];
        setPendingHiddenOptionIds((prev) => {
          if (!prev[optionId]) return prev;
          const next = { ...prev };
          delete next[optionId];
          return next;
        });
      }
    });

    previousEnabledByIdRef.current = nextEnabledById;

    if (!isTemplateHideMode && Object.keys(pendingHiddenOptionIds).length > 0) {
      Object.values(hideTimeoutsRef.current).forEach((timeoutId) => clearTimeout(timeoutId));
      hideTimeoutsRef.current = {};
      setPendingHiddenOptionIds({});
    }
  }, [options, questionLanguage, inTemplateEdit, showDisabledOptions, pendingHiddenOptionIds]);

  const visibleOptions =
    inTemplateEdit && !showDisabledOptions
      ? options?.filter((option) => option.enabled?.[questionLanguage] !== false || pendingHiddenOptionIds[option.id])
      : options;

  const toFullOptionsOrder = (sortedVisibleOptions: typeof options) => {
    if (!options) return sortedVisibleOptions;

    if (!inTemplateEdit || showDisabledOptions) {
      return sortedVisibleOptions;
    }

    let visibleIndex = 0;

    return options.map((option) => {
      const isVisible = option.enabled?.[questionLanguage] !== false;
      if (!isVisible) return option;

      const nextVisible = sortedVisibleOptions?.[visibleIndex];
      visibleIndex += 1;
      return nextVisible ?? option;
    });
  };

  const handleOptionDragUpdate = (items: any, containers: string[]) => {
    const updatedOptions = toFullOptionsOrder(items.options?.items);
    const updatedItems = {
      ...items,
      options: items.options
        ? {
            ...items.options,
            items: updatedOptions,
          }
        : items.options,
    };

    dispatch(dragAndDropOption({ questionId, items: updatedItems, containerIds: containers, mode, type }));
    if (mode === ECreateQuestionMode.InTemplateEdit && updatedOptions) {
      updateInTemplateOptionOrder(templateId, questionId, updatedOptions, templateOptions, dispatch);
    }
  };

  if (!type) return null;
  if (errors?.length) errors.map((x) => toast.error(x));

  if (type === EQuestionType.SingleSelect) {
    // Skip dnd-kit (which measures every item up front) for searchable-list questions, and as a
    // safety net for any question sitting on too many options regardless of display mode — that
    // case would otherwise be unopenable, with no way to reach the toggle that'd fix it.
    const hasTooManyOptionsForDragAndDrop = (options?.length ?? 0) > OPTIONS_PAGE_SIZE;
    const useCappedRendering = displayMode === QUESTION_DISPLAY_MODE.SearchableList || hasTooManyOptionsForDragAndDrop;
    const showOptionsSearch = (visibleOptions?.length ?? 0) > OPTIONS_PAGE_SIZE;
    const searchQuery = showOptionsSearch ? normalizeForSearch(optionsQuery.trim()) : '';
    const matchingOptions = searchQuery
      ? visibleOptions?.filter((option) =>
          normalizeForSearch(option.label?.[questionLanguage] ?? '').includes(searchQuery),
        )
      : visibleOptions;
    const totalVisibleOptions = matchingOptions?.length ?? 0;
    const pageCount = Math.max(1, Math.ceil(totalVisibleOptions / OPTIONS_PAGE_SIZE));
    const currentPage = Math.min(optionsPage, pageCount - 1);
    const pageStart = currentPage * OPTIONS_PAGE_SIZE;
    const optionsToRender = useCappedRendering
      ? matchingOptions?.slice(pageStart, pageStart + OPTIONS_PAGE_SIZE)
      : visibleOptions;
    const handlePageChange = (page: number) => {
      setOptionsPage(page);
      optionsListRef.current?.scrollIntoView({ block: 'start' });
    };

    return (
      <div className={clsx('p-1')}>
        <SingleSelectDisplayModeSelect />
        <PasteOptionsList />
        {useCappedRendering ? (
          <>
            <CreateOptionHeader />
            {showOptionsSearch ? (
              <HubertInput
                type="search"
                value={optionsQuery}
                placeholder={t('typeSpecificEditor.search.placeholder')}
                aria-label={t('typeSpecificEditor.search.placeholder')}
                onChange={(e) => {
                  setOptionsQuery(e.target.value);
                  setOptionsPage(0);
                }}
                className="mx-2 my-2 w-64"
              />
            ) : null}
            {searchQuery && totalVisibleOptions === 0 ? (
              <MutedText text={t('typeSpecificEditor.search.noResults')} className="p-2" />
            ) : null}
            <div ref={optionsListRef} className={clsx('flex flex-col')}>
              {optionsToRender?.map((item, index) => (
                <CreateOption
                  key={item.id}
                  data={{ item: item as TSingleSelectOption, index: pageStart + index }}
                  evaluation={evaluation}
                  currentRules={currentRules}
                  inTemplateEditAndUneditable={inTemplateEditAndUneditable}
                />
              ))}
            </div>
            {pageCount > 1 ? (
              <OptionsPagination
                page={currentPage}
                pageCount={pageCount}
                pageSize={OPTIONS_PAGE_SIZE}
                total={totalVisibleOptions}
                onPageChange={handlePageChange}
              />
            ) : null}
          </>
        ) : (
          <TextBubbleDragAndDrop
            disabled={isReadOnly}
            onShouldUpdate={handleOptionDragUpdate}
            input={{
              options: visibleOptions
                ? {
                    id: 'options',
                    headerName: t('typeSpecificEditor.questionOptions'),
                    items: visibleOptions,
                  }
                : undefined,
            }}
            headerContent={(data) => {
              return <CreateOptionHeader data={data} />;
            }}
            itemContent={(data) => (
              <CreateOption
                data={data}
                evaluation={evaluation}
                currentRules={currentRules}
                inTemplateEditAndUneditable={inTemplateEditAndUneditable}
              />
            )}
          />
        )}
        {isDeveloper(user) ? <QuestionVoiceSettings /> : null}
      </div>
    );
  }

  if (type === EQuestionType.MultipleSelect) {
    return (
      <div className={clsx('flex flex-col justify-between gap-16', 'p-1')}>
        <CreateOptionOptions />
        <HelpTextInput />
        <TextBubbleDragAndDrop
          disabled={isReadOnly}
          onShouldUpdate={handleOptionDragUpdate}
          input={{
            options: visibleOptions
              ? {
                  id: 'options',
                  headerName: t('typeSpecificEditor.questionOptions'),
                  items: visibleOptions,
                }
              : undefined,
          }}
          headerContent={(data) => {
            return <CreateOptionHeader data={data} />;
          }}
          itemContent={(data: any) => (
            <CreateOption
              data={data}
              evaluation={evaluation}
              currentRules={currentRules}
              inTemplateEditAndUneditable={inTemplateEditAndUneditable}
            />
          )}
        />
        {mode === ECreateQuestionMode.InTemplateEdit && <SelectionCountPreview nodeId={questionId} />}
      </div>
    );
  }

  if (type === EQuestionType.Ranking) {
    return (
      <div className={clsx('flex flex-col justify-between gap-16', 'p-1')}>
        <RankingModeSelect />
        <TitleInput placeholder={t('typeSpecificEditor.rankingPlaceholder')} />
        <HelpTextInput />
        <RandomizeOrderSwitch />
        <NumberSettingInput />
        <TextBubbleDragAndDrop
          disabled={isReadOnly}
          onShouldUpdate={handleOptionDragUpdate}
          input={{
            options: visibleOptions
              ? {
                  id: 'options',
                  headerName: t('typeSpecificEditor.rankingOptions'),
                  items: visibleOptions,
                }
              : undefined,
          }}
          headerContent={(data) => {
            return <CreateRankingOptionHeader data={data} />;
          }}
          itemContent={(data: any) => <CreateRankingOption data={data} />}
        />
      </div>
    );
  }

  if (type === EQuestionType.Audio || type === EQuestionType.Video) {
    return <CreateRecording />;
  }

  if (type === EQuestionType.Open) {
    return <CreateOpen />;
  }

  if (type === EQuestionType.Info) {
    return <CreateInfo />;
  }

  if (type === EQuestionType.Form) {
    return (
      <div className={clsx('p-1')}>
        <TextBubbleDragAndDrop
          disabled={isReadOnly}
          onShouldUpdate={(items, containers) => {
            dispatch(dragAndDropForm({ questionId, items, containerIds: containers, mode, type }));
          }}
          input={{
            forms: forms
              ? {
                  id: 'forms',
                  headerName: t('typeSpecificEditor.questionForms'),
                  items: forms,
                }
              : undefined,
          }}
          headerContent={(data) => {
            return <CreateFormHeader data={data} />;
          }}
          itemContent={(data) => {
            return <FormElement data={data} />;
          }}
        />
      </div>
    );
  }

  if (type === EQuestionType.Scheduling) {
    return <CreateScheduling />;
  }

  return null;
};

export default TypeSpecificEditor;
